/**
 * @fileoverview Hook for student activity page
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { fetchStudentProjects } from './api'

type Tab = 'assigned' | 'completed'

const TAB_TO_STATUS = { assigned: 'approved', completed: 'finished' } as const

/** Safely transforms API project to display format */
function toDisplayProject(p: Record<string, unknown>) {
  const groupSchoolTerm = p?.groupSchoolTerm as { name?: string } | null | undefined
  const grades = (p?.grades as Array<{ name?: string }>) ?? []
  const author = p?.author as { firstName?: string; lastName?: string } | undefined
  const updatedAt = p?.updatedAt as string | undefined

  const authorName = author
    ? `${author.firstName ?? ''} ${author.lastName ?? ''}`.trim() || 'Unknown'
    : 'Unknown'

  return {
    id: String(p?.id ?? Math.random()),
    name: String(p?.name ?? 'Untitled'),
    className: groupSchoolTerm?.name ?? '—',
    gradeName: grades[0]?.name ?? '—',
    createdBy: authorName,
    updatedAt: updatedAt ?? '',
  }
}

export function useStudentActivity(studentId: string | undefined) {
  const [tab, setTab] = useState<Tab>('assigned')
  const [page, setPage] = useState(1)
  const [projects, setProjects] = useState<
    Array<{ id: string; name: string; className: string; gradeName: string; createdBy: string; updatedAt: string }>
  >([])
  const [pagination, setPagination] = useState({ count: 0, page: 1, pages: 1 })
  const [counts, setCounts] = useState({ assigned: 0, completed: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const mountedRef = useRef(true)
  const abortRef = useRef<AbortController | null>(null)

  // Fetch counts on mount (both tabs) - only if studentId exists
  useEffect(() => {
    if (!studentId) return
    Promise.all([
      fetchStudentProjects(studentId, 'approved', 1, 1),
      fetchStudentProjects(studentId, 'finished', 1, 1),
    ])
      .then(([assigned, completed]) => {
        if (mountedRef.current) {
          setCounts({
            assigned: assigned.pagination.count,
            completed: completed.pagination.count,
          })
        }
      })
      .catch(() => {
        // Silently fail - counts will stay at 0
      })
  }, [studentId])

  // Fetch projects on tab/page change
  useEffect(() => {
    if (!studentId) {
      setLoading(false)
      setError('Student ID is required')
      return
    }

    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    setError(null)

    fetchStudentProjects(studentId, TAB_TO_STATUS[tab], page)
      .then(data => {
        if (ctrl.signal.aborted || !mountedRef.current) return
        setProjects(data.projects.map(p => toDisplayProject(p as Record<string, unknown>)))
        setPagination(data.pagination)
        setCounts(c => ({ ...c, [tab]: data.pagination.count }))
      })
      .catch(() => {
        if (ctrl.signal.aborted || !mountedRef.current) return
        setError('Failed to load projects')
        setProjects([])
      })
      .finally(() => {
        if (!ctrl.signal.aborted && mountedRef.current) setLoading(false)
      })

    return () => ctrl.abort()
  }, [studentId, tab, page])

  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  const changeTab = useCallback((t: Tab) => {
    setTab(t)
    setPage(1)
  }, [])

  return { tab, page, projects, pagination, counts, loading, error, setTab: changeTab, setPage }
}
